"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Facebook, Linkedin, Youtube } from "lucide-react";
import AlexARLogo from "../icons/AlexARLogo";
import CustomButton from "./CustomButton";
import CallExpandBtn from "./CallExpandBtn";
import FooterAddress from "./FooterAddress";
import { FOOTER_COPYRIGHT_BG_HEX, FOOTER_COPYRIGHT_TEXT_HEX, THEME_HEX } from "./theme";
import { getPageTheme } from "@/constants/pageThemes";
import { SITE_URL, ORGANIZATION_NAME, metaAssets } from "@/lib/metadata";
import { trackBookAppointmentClicked } from "@/lib/analytics";

const HEADING_CLASS = "font-crimsonText text-[20px] font-bold mb-3";
const LINK_CLASS = "font-onest text-[16px] font-normal hover:underline leading-[1.8]";

const navColumns = [
  {
    heading: "Navigations",
    links: [
      { label: "Home", href: "/" },
      { label: "About", href: "/about" },
      { label: "Our Team", href: "/teams" },
      { label: "FAQ", href: "/counselling-faq" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
  {
    heading: "Counselling",
    links: [
      { label: "Anger Management", href: "/anger-management" },
      { label: "Relationships", href: "/relationships" },
      {
        label: "Depression & Anxiety",
        href: "/depression-anxiety",
      },
      { label: "Grief & Loss", href: "/grief-loss" },
      { label: "Career Counselling", href: "/career-counselling" },
    ],
  },
  {
    heading: "Life Coaching",
    links: [
      { label: "Self Esteem & Confidence", href: "/self-esteem-confidence" },
      { label: "Time Management", href: "/time-management" },
      { label: "Focus & Productivity", href: "/focus-productivity" },
      { label: "Changing Habits", href: "/changing-habits" },
      { label: "Public Speaking", href: "/public-speaking" },
    ],
  },
  {
    heading: "Allied Health",
    links: [
      {
        label: "Occupational Therapy",
        href: "/occupational-therapy",
      },
      { label: "Psychology", href: "/psychology" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Podcast", href: "/podcast" },
      { label: "Wellness Blog", href: "/wellness-blog" },
      { label: "24/7 Self Discovery", href: "/24seven-self-discovery" },
      { label: "Mini Games", href: "/mini-games" },
    ],
  },
];

const companyLinks = [
  { label: "Terms of Use", href: "/terms-of-use" },
  { label: "Privacy Policy", href: "/privacy-policy" },
];

const socials = [
  {
    Icon: Facebook,
    href: "https://www.facebook.com/profile.php?id=100086562936568",
    label: "Facebook",
  },
  {
    Icon: Youtube,
    href: "https://www.youtube.com/@Dose-Of-Growth",
    label: "Youtube",
  },
  {
    Icon: Linkedin,
    href: "https://www.linkedin.com/in/alexander-rodriguez-7b551893/",
    label: "LinkedIn",
  },
];

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: ORGANIZATION_NAME,
  url: SITE_URL,
  logo: new URL(metaAssets.icons.icon512, SITE_URL).href,
  telephone: "+61429220646",
  sameAs: socials.map(({ href }) => href),
};

const Footer = () => {
  const pathname = usePathname();
  const theme = getPageTheme(pathname);
  const color = THEME_HEX[theme];

  return (
    <footer>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema).replace(/</g, "\\u003c"),
        }}
      />
      {/* ── Main content ── */}
      <div className="container bg-white">
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-[50px]">
          {/* Logo — scales down on mobile */}
          <div className="mx-auto w-[140px] shrink-0 lg:mx-0 lg:w-[265px]">
            <AlexARLogo
              width={265}
              height={225}
              color={color}
              className="h-auto w-full"
            />
          </div>

          {/* Right content */}
          <div className="flex flex-1 flex-col gap-8 lg:gap-12">
            {/* Nav columns — 2-col on mobile, 3-col on sm, 5-col on desktop */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-5 lg:gap-x-6 lg:gap-y-0">
              {navColumns.map(({ heading, links }) => (
                <div key={heading} className="flex flex-col">
                  <span className={HEADING_CLASS} style={{ color }}>
                    {heading}
                  </span>
                  <ul className="flex flex-col">
                    {links.map(({ label, href }) => (
                      <li key={label}>
                        <Link href={href} className={LINK_CLASS} style={{ color }}>
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Bottom row — same 5-col grid so columns align with nav above */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 lg:items-start lg:gap-x-6">
              {/* Company: col 1 */}
              <div className="flex flex-col">
                <span className={HEADING_CLASS} style={{ color }}>
                  Company
                </span>
                <ul className="flex flex-col">
                  {companyLinks.map(({ label, href }) => (
                    <li key={label}>
                      <Link href={href} className={LINK_CLASS} style={{ color }}>
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Location + Socials: col 2 */}
              <div className="flex flex-col gap-4">
                <FooterAddress theme={theme} />
                <div className="flex items-center gap-3">
                  {socials.map(({ Icon, href, label }) => (
                    <Link
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="transition-opacity hover:opacity-70"
                      style={{ color }}
                    >
                      <Icon size={20} />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Booking: cols 3-5 */}
              <div className="flex flex-col gap-5 sm:col-span-2 lg:col-span-3">
                <span className={HEADING_CLASS} style={{ color }}>
                  Book an Appointment
                </span>
                <p
                  className="font-onest text-[16px] font-normal leading-[1.6]"
                  style={{ color }}
                >
                  Taking the first step can be hard, but you&apos;re not alone.
                  If you&apos;re looking for support, reach out to book a safe
                  and supportive session.
                </p>
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-4">
                  <CallExpandBtn theme={theme} />
                  <CustomButton
                    label="Book an Appointment"
                    href="/booking"
                    variant="primary"
                    theme={theme}
                    withIcon
                    size="lg"
                    onClick={() => trackBookAppointmentClicked({ location: "footer" })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Copyright bar ── */}
      <div
        className="px-5 py-3 text-center"
        style={{ backgroundColor: FOOTER_COPYRIGHT_BG_HEX[theme] }}
      >
        <p
          className="font-onest text-[13px] font-normal"
          style={{ color: FOOTER_COPYRIGHT_TEXT_HEX[theme] }}
        >
          © 2025 Alex Rodriguez. All rights reserved. Web Design by{" "}
          <Link
            href="https://strategybyte.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-70"
          >
            Strategybyte
          </Link>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
